import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { listVoyages, deleteVoyage } from "@/lib/voyagesApi";
import { formatDate, endDate } from "@/lib/dateHelpers";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from "@/components/ui/table";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";
import DuplicateDialog from "@/components/DuplicateDialog";
import {
  Plus, Loader2, Search, Ship, Copy, Trash2, ExternalLink, Anchor,
} from "lucide-react";

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived" },
];

const statusColor = (s) => {
  if (s === "active") return "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (s === "archived") return "bg-gray-100 text-gray-600 border-gray-200";
  return "bg-amber-50 text-amber-700 border-amber-200";
};

export default function VoyagesList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [voyages, setVoyages] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const statusFilter = searchParams.get("status") || "all";
  const [search, setSearch] = useState("");
  const page = parseInt(searchParams.get("page") || "1", 10);
  const PAGE_SIZE = 50;

  // Delete dialog
  const [delOpen, setDelOpen] = useState(false);
  const [delItem, setDelItem] = useState(null);
  const [delLoading, setDelLoading] = useState(false);

  // Duplicate dialog
  const [dupOpen, setDupOpen] = useState(false);
  const [dupItem, setDupItem] = useState(null);

  const fetchVoyages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE };
      if (statusFilter !== "all") params.status = statusFilter;
      const data = await listVoyages(params);
      setVoyages(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page]);

  useEffect(() => { fetchVoyages(); }, [fetchVoyages]);

  const handleStatusChange = (val) => {
    const p = new URLSearchParams(searchParams);
    if (val === "all") p.delete("status"); else p.set("status", val);
    p.delete("page");
    setSearchParams(p);
  };

  const handleDelete = async () => {
    if (!delItem) return;
    setDelLoading(true);
    try {
      await deleteVoyage(delItem.id);
      setDelOpen(false);
      setDelItem(null);
      fetchVoyages();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setDelLoading(false);
    }
  };

  const filtered = search
    ? voyages.filter(
        (v) =>
          v.voyage_number?.toLowerCase().includes(search.toLowerCase()) ||
          v.ship_name?.toLowerCase().includes(search.toLowerCase())
      )
    : voyages;

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="px-6 py-8" data-testid="voyages-list-page">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="voyages-title">Voyages</h1>
          <p className="text-sm text-muted-foreground">
            Manage cruise voyages and their itineraries.
            {!loading && <span className="ml-1 text-xs">({total} total)</span>}
          </p>
        </div>
        <Link to="/voyages/new">
          <Button data-testid="new-voyage-btn">
            <Plus className="h-4 w-4" />
            New Voyage
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
        <Select value={statusFilter} onValueChange={handleStatusChange}>
          <SelectTrigger className="w-[180px]" data-testid="voyage-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by voyage number or ship..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
            data-testid="voyage-search-input"
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive" data-testid="voyages-error">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Empty state */}
      {!loading && filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center" data-testid="voyages-empty">
          <Anchor className="mb-3 h-10 w-10 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No voyages yet — create one to get started.</p>
          <Link to="/voyages/new">
            <Button size="sm" className="mt-4" data-testid="voyages-empty-create">
              <Plus className="h-4 w-4" /> New Voyage
            </Button>
          </Link>
        </div>
      )}

      {/* Table */}
      {!loading && filtered.length > 0 && (
        <div className="rounded-lg border" data-testid="voyages-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Voyage #</TableHead>
                <TableHead>Ship</TableHead>
                <TableHead>Dates</TableHead>
                <TableHead className="text-center">Days</TableHead>
                <TableHead className="text-center">Guests</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((v) => (
                <TableRow key={v.id} data-testid={`voyage-row-${v.id}`}>
                  <TableCell className="font-medium">{v.voyage_number}</TableCell>
                  <TableCell>
                    <span className="flex items-center gap-1.5">
                      <Ship className="h-3.5 w-3.5 text-muted-foreground" />
                      {v.ship_name || "—"}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">
                    {formatDate(v.start_date)}
                    <span className="mx-1 text-muted-foreground">→</span>
                    {formatDate(endDate(v.start_date, v.total_days))}
                  </TableCell>
                  <TableCell className="text-center">{v.total_days}</TableCell>
                  <TableCell className="text-center">
                    {v.guest_count || 0}
                    {v.kids_count > 0 && (
                      <span className="text-xs text-muted-foreground ml-1">+{v.kids_count}k</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={statusColor(v.status)}>
                      {v.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Link to={`/voyages/${v.id}`}>
                        <Button variant="ghost" size="icon" data-testid={`open-voyage-${v.id}`} aria-label="Open voyage">
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </Link>
                      <Button
                        variant="ghost" size="icon"
                        data-testid={`dup-voyage-${v.id}`}
                        aria-label="Duplicate voyage"
                        onClick={() => { setDupItem(v); setDupOpen(true); }}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost" size="icon"
                        data-testid={`del-voyage-${v.id}`}
                        aria-label="Delete voyage"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => { setDelItem(v); setDelOpen(true); }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <Button
            variant="outline" size="sm" disabled={page <= 1}
            onClick={() => { const p = new URLSearchParams(searchParams); p.set("page", String(page - 1)); setSearchParams(p); }}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
          <Button
            variant="outline" size="sm" disabled={page >= totalPages}
            onClick={() => { const p = new URLSearchParams(searchParams); p.set("page", String(page + 1)); setSearchParams(p); }}
          >
            Next
          </Button>
        </div>
      )}

      {/* Dialogs */}
      <DeleteConfirmDialog
        open={delOpen} onOpenChange={setDelOpen}
        itemName={delItem?.voyage_number} loading={delLoading}
        onConfirm={handleDelete}
      />
      <DuplicateDialog
        open={dupOpen} onOpenChange={setDupOpen}
        voyage={dupItem} onSuccess={() => { setDupOpen(false); fetchVoyages(); }}
      />
    </div>
  );
}
